import { useState, useEffect } from "react";
import { useSearchParams, useNavigate } from "react-router-dom";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Users, AlertCircle, Building2 } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "@/hooks/use-toast";
import { PageWrapper } from "@/components/layout/PageWrapper";

// Interface pour le retour de la RPC get_invitation_context
interface InvitationContext {
  error: boolean;
  code?: string;
  message?: string;
  invitation?: {
    id: string;
    email: string;
    first_name?: string;
    last_name?: string;
    role: string;
  };
  organization?: {
    id: string;
    name: string;
  };
  existing_profile?: {
    id: string;
    first_name: string;
    last_name: string;
    company: string;
    position: string;
  } | null;
  user_exists: boolean;
}

export default function InviteSignup() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const token = searchParams.get("token");
  
  const [loading, setLoading] = useState(true);
  const [creating, setCreating] = useState(false);
  const [resettingPassword, setResettingPassword] = useState(false);
  const [invitation, setInvitation] = useState<any>(null);
  const [organization, setOrganization] = useState<any>(null);
  const [error, setError] = useState<string | null>(null);
  
  // Type d'utilisateur: 'new' ou 'existing'
  const [userType, setUserType] = useState<'new' | 'existing' | null>(null);
  const [existingProfile, setExistingProfile] = useState<any>(null);
  
  // Formulaire
  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [firstName, setFirstName] = useState("");
  const [lastName, setLastName] = useState("");
  const [position, setPosition] = useState("");

  useEffect(() => {
    if (!token) {
      setError("Token d'invitation manquant");
      setLoading(false);
      return;
    }
    
    const loadData = async () => {
      try {
        // Appel RPC unique pour récupérer tout le contexte
        const { data: context, error: rpcError } = await supabase
          .rpc("get_invitation_context", { invitation_token: token || "" }) as { data: InvitationContext | null, error: any };

        if (rpcError) {
          console.error("Error calling get_invitation_context RPC", rpcError);
          setError("Erreur lors du chargement de l'invitation");
          setLoading(false);
          return;
        }

        // Vérifier si la RPC a retourné une erreur
        if (context?.error) {
          setError(context.message || "Invitation introuvable");
          setLoading(false);
          return;
        }

        if (!context || !context.invitation || !context.organization) {
          setError("Invitation introuvable");
          setLoading(false);
          return;
        }

        // Extraire les données
        const { invitation: invitationData, organization: orgData, existing_profile, user_exists } = context;

        setInvitation(invitationData);
        setOrganization(orgData);

        // Détection automatique du type d'utilisateur
        if (user_exists && existing_profile) {
          // Utilisateur existant
          setUserType('existing');
          setExistingProfile(existing_profile);
          // Pré-remplir avec profil existant
          setFirstName(existing_profile.first_name);
          setLastName(existing_profile.last_name);
          setPosition(existing_profile.position);
        } else {
          // Nouvel utilisateur
          setUserType('new');
          // Pré-remplir avec invitation
          setFirstName(invitationData.first_name || '');
          setLastName(invitationData.last_name || '');
        }

        setLoading(false);
      } catch (err) {
        console.error("Error loading invitation context", err);
        setError("Erreur lors du chargement de l'invitation");
        setLoading(false);
      }
    };
    
    loadData();
  }, [token]);



  const handleForgotPassword = async () => {
    if (!invitation?.email) return;
    
    setResettingPassword(true);
    
    try {
      // Appeler resetPasswordForEmail au lieu de juste naviguer
      const { error } = await supabase.auth.resetPasswordForEmail(
        invitation.email,
        {
          redirectTo: `${window.location.origin}/auth/reset-password`
        }
      );
      
      if (error) {
        toast({
          title: "Erreur",
          description: "Impossible d'envoyer l'email de réinitialisation",
          variant: "destructive"
        });
        return;
      }
      
      // Toast de confirmation
      toast({
        title: "Email envoyé",
        description: `Un email de réinitialisation a été envoyé à ${invitation.email}. Vérifiez votre boîte de réception.`,
      });
      
    } catch (err: any) {
      console.error("Error sending reset password email", err);
      toast({
        title: "Erreur",
        description: err.message || "Erreur lors de l'envoi de l'email",
        variant: "destructive"
      });
    } finally {
      setResettingPassword(false);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!invitation) return;

    // Validation mot de passe pour nouveaux utilisateurs
    if (userType === 'new') {
      if (password !== confirmPassword) {
        toast({
          title: "Erreur",
          description: "Les mots de passe ne correspondent pas",
          variant: "destructive"
        });
        return;
      }

      if (password.length < 6) {
        toast({
          title: "Erreur", 
          description: "Le mot de passe doit contenir au moins 6 caractères",
          variant: "destructive"
        });
        return;
      }
    }

    setCreating(true);

    try {
      if (userType === 'existing') {
        // Cas 1: Connexion utilisateur existant
        const { error } = await supabase.auth.signInWithPassword({
          email: invitation.email,
          password
        });
        
        if (error) {
          // Message d'aide si échec
          toast({
            title: "Erreur de connexion",
            description: "Mot de passe incorrect. Si vous n'avez jamais défini de mot de passe, utilisez 'Mot de passe oublié'.",
            variant: "destructive"
          });
          setCreating(false);
          return;
        }
        
        // Redirection immédiate avec token
        const tokenParam = token || "";
        navigate(`/dashboard?invite_token=${encodeURIComponent(tokenParam)}`);
        
      } else {
        // Cas 2: Création nouveau compte
        const { data: authData, error: authError } = await supabase.auth.signUp({
          email: invitation.email,
          password,
          options: {
            data: {
              first_name: firstName,
              last_name: lastName, 
              company: organization?.name, // Utiliser le nom de l'organisation
              position
            }
          }
        });

        if (authError) {
          // Vérifier si l'utilisateur existe déjà
          if (authError.message.includes("already registered") || authError.message.includes("User already registered")) {
            // Compte existant → Rediriger vers connexion avec invite_token
            toast({
              title: "Compte existant",
              description: "Un compte existe déjà. Redirection vers la connexion...",
            });
            
            const tokenParam = token || "";
            navigate(
              `/auth?invite_token=${encodeURIComponent(tokenParam)}&email=${encodeURIComponent(invitation.email)}`
            );
            return;
          } else {
            throw authError;
          }
        }

        // Compte créé avec succès
        const tokenParam = token || "";
        if (authData.session) {
          // Session créée → email confirmé automatiquement
          navigate(`/dashboard?invite_token=${encodeURIComponent(tokenParam)}`);
        } else {
          // Pas de session → confirmation email requise
          toast({
            title: "Compte créé",
            description: "Vérifiez votre email pour confirmer votre compte, puis revenez via le lien d'invitation.",
          });
          
          navigate(
            `/auth?invite_token=${encodeURIComponent(tokenParam)}&email=${encodeURIComponent(invitation.email)}`
          );
        }
      }

    } catch (err: any) {
      console.error("Error during submit", err);
      toast({
        title: "Erreur",
        description: err.message || "Erreur lors de la soumission",
        variant: "destructive"
      });
      setCreating(false);
    }
  };

  if (loading) {
    return (
      <PageWrapper layout="no-sidebar">
        <div className="min-h-screen bg-[#f8f9fc] flex items-center justify-center">
          <Card className="w-full max-w-md">
            <CardContent className="flex items-center justify-center p-6">
              <div className="flex items-center gap-2">
                <div className="w-5 h-5 border-2 border-primary border-t-transparent rounded-full animate-spin" />
                <span>Chargement...</span>
              </div>
            </CardContent>
          </Card>
        </div>
      </PageWrapper>
    );
  }

  if (error) {
    return (
      <PageWrapper layout="no-sidebar">
        <div className="min-h-screen bg-[#f8f9fc] flex items-center justify-center">
          <Card className="w-full max-w-md">
            <CardHeader className="text-center">
              <div className="mx-auto w-12 h-12 bg-destructive/10 rounded-full flex items-center justify-center mb-4">
                <AlertCircle className="w-6 h-6 text-destructive" />
              </div>
              <CardTitle className="text-destructive">Invitation invalide</CardTitle>
              <CardDescription>{error}</CardDescription>
            </CardHeader>
            <CardContent>
              <Button onClick={() => navigate("/auth")} className="w-full">
                Se connecter
              </Button>
            </CardContent>
          </Card>
        </div>
      </PageWrapper>
    );
  }

  return (
    <PageWrapper layout="no-sidebar">
      <div className="min-h-screen bg-[#f8f9fc] flex items-center justify-center p-4">
        <Card className="w-full max-w-md">
          <CardHeader className="text-center">
            <div className="mx-auto w-12 h-12 bg-primary/10 rounded-full flex items-center justify-center mb-4">
              <Users className="w-6 h-6 text-primary" />
            </div>
            
            <CardTitle>
              {userType === 'existing' ? 'Rejoindre l\'organisation' : 'Créer votre compte'}
            </CardTitle>
            
            <CardDescription>
              {userType === 'existing' 
                ? 'Vous avez déjà un compte. Connectez-vous pour rejoindre l\'organisation.'
                : 'Créez votre compte pour rejoindre l\'organisation.'
              }
            </CardDescription>
            
            {/* Afficher l'organisation comme badge/info séparée */}
            <div className="flex items-center justify-center gap-2 mt-3 p-3 bg-primary/5 rounded-lg">
              <Building2 className="w-4 h-4 text-primary" />
              <span className="text-sm font-medium">{organization?.name}</span>
            </div>
            
            <div className="flex items-center justify-center gap-2 mt-2">
              <span className="text-sm text-muted-foreground">Email:</span>
              <span className="text-sm font-medium">{invitation?.email}</span>
            </div>
            
            <div className="flex items-center justify-center gap-2">
              <span className="text-sm text-muted-foreground">Rôle:</span>
              <Badge variant={invitation?.role === "admin" ? "default" : "secondary"}>
                {invitation?.role === "admin" ? "Administrateur" : "Membre"}
              </Badge>
            </div>
          </CardHeader>
          
          <CardContent>
            <form onSubmit={handleSubmit} className="space-y-4">
              {/* Prénom et Nom */}
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label htmlFor="firstName">Prénom</Label>
                  <Input
                    id="firstName"
                    type="text"
                    placeholder="Jean"
                    value={firstName}
                    onChange={(e) => setFirstName(e.target.value)}
                    disabled={userType === 'existing'}
                    className={userType === 'existing' ? "bg-muted" : ""}
                    required
                  />
                </div>
                <div>
                  <Label htmlFor="lastName">Nom</Label>
                  <Input
                    id="lastName"
                    type="text"
                    placeholder="Dupont"
                    value={lastName}
                    onChange={(e) => setLastName(e.target.value)}
                    disabled={userType === 'existing'}
                    className={userType === 'existing' ? "bg-muted" : ""}
                    required
                  />
                </div>
              </div>

              {/* Société (uniquement si utilisateur existant, pour info) */}
              {userType === 'existing' && existingProfile?.company && (
                <div>
                  <Label htmlFor="company">Société actuelle</Label>
                  <Input
                    id="company"
                    type="text"
                    value={existingProfile.company}
                    disabled
                    className="bg-muted"
                  />
                  <p className="text-xs text-muted-foreground mt-1">
                    Informations de votre profil. Modifiables dans les paramètres après connexion.
                  </p>
                </div>
              )}

              {/* Poste */}
              <div>
                <Label htmlFor="position">Poste</Label>
                <Input
                  id="position"
                  type="text"
                  placeholder="Votre poste"
                  value={position}
                  onChange={(e) => setPosition(e.target.value)}
                  disabled={userType === 'existing'}
                  className={userType === 'existing' ? "bg-muted" : ""}
                  required
                />
              </div>

              {/* Mot de passe */}
              <div>
                <Label htmlFor="password">Mot de passe</Label>
                <Input
                  id="password"
                  type="password"
                  placeholder={userType === 'existing' ? "Votre mot de passe" : "Minimum 6 caractères"}
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  required
                  minLength={6}
                />
              </div>

              {/* Confirmation mot de passe uniquement pour nouveaux utilisateurs */}
              {userType === 'new' && (
                <div>
                  <Label htmlFor="confirmPassword">Confirmer le mot de passe</Label>
                  <Input
                    id="confirmPassword"
                    type="password"
                    placeholder="Confirmez votre mot de passe"
                    value={confirmPassword}
                    onChange={(e) => setConfirmPassword(e.target.value)}
                    required
                    minLength={6}
                  />
                </div>
              )}

              {/* Bouton "Mot de passe oublié" pour utilisateurs existants */}
              {userType === 'existing' && (
                <div className="text-center">
                  <Button
                    type="button"
                    variant="link"
                    onClick={handleForgotPassword}
                    disabled={resettingPassword}
                    className="text-sm"
                  >
                    {resettingPassword ? "Envoi en cours..." : "Mot de passe oublié ?"}
                  </Button>
                </div>
              )}

              {/* Bouton principal avec loading state */}
              <Button 
                type="submit"
                disabled={creating}
                className="w-full"
              >
                {creating 
                  ? (userType === 'existing' ? "Connexion..." : "Création...") 
                  : (userType === 'existing' ? "Se connecter et rejoindre" : "Créer le compte")
                }
              </Button>
            </form>
          </CardContent>
        </Card>
      </div>
    </PageWrapper>
  );
}
